import { FC, Fragment, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { CommonStyle, ControlledTable, Filter2 } from "Components";
import { actionArr, FilterArr, TableCol } from "./Data";
import CommonFormatter from "Utils/Common.Formatter";
import { RouteUrls } from "Constant/RouteUrls";
import {
  HeaderInitialStateType,
  ScreenInitialStateType
} from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PreApprovedPlanApi from "Service/PreApprovedPlan.api";
import PreApprovedPlanFormatter from "Utils/PreApprovedPlan.Formatter";
import { PreApprovedPlanListType } from "DataTypes/PatientManagement.type";
import { ModalLayout, TableLayout } from "Layouts";
import { Filter2DataProps } from "DataTypes/Common";
import {
  CreateFilter2Payload,
  RedoxDataToFilter,
  TransformFilterDataRedox
} from "Utils/common";
import { useDispatch } from "react-redux";
import UI from "Constant/UI";
import { setAlertData, setPageFilter, setPageLimit } from "Redux/Screen/Action";
import { CommonButtonArray } from "Components/Modal/Data";

const PreApprovedPlan: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { organization }: HeaderInitialStateType = useSelector(
    (state: any) => state?.header
  );
  const { listPagination, listFilters }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const listObj = listFilters.find(el => el.id == "org-pre-approved-plan");
  const reduxLimit = (listPagination || []).find(
    el => el.id == "org-pre-approved-plan"
  )?.limit || 10;
  const [rows, setRows] = useState<PreApprovedPlanListType[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterHeight, setFilterHeight] = useState<number>(0);
  const [filterData, setFilterData] = useState<Filter2DataProps[]>(() =>
    RedoxDataToFilter(
      JSON.parse(JSON.stringify(FilterArr())),
      listObj
    )
  );
  const [pageData, setPageData] = useState({
    ...UI.pageData,
    limit: reduxLimit
  });
  const [searchText, setSearchText] = useState("");
  const [disablePlan, setDisablePlan] =
    useState<PreApprovedPlanListType | null>(null);
  const [enablePlan, setEnablePlan] =
    useState<PreApprovedPlanListType | null>(null);
  const tableHeight = `calc(100% - ${filterHeight}px)`;

  useEffect(() => {
    handleSetData(1, pageData.limit);
  }, [filterData, organization.id]);

  useEffect(() => {
    CommonFormatter.HandleNavArr({
      id: "pre-approved-plan",
      label: `${organization.name}'s Pre Approved Plans`,
      path: `/${RouteUrls.hs}/${RouteUrls.organization}` +
        `/${organization.id}/${RouteUrls.preApprovedPlan}/${RouteUrls.list}`,
      level: 2,
      link: RouteUrls.list,
      navType: "org",
    });
  }, [organization.id]);

  const handleSetData: (
    currentPage: number,
    currentLimit: number
  ) => void = async (currentPage, currentLimit) => {
    setLoading(true);
    const newFilterPayload = CreateFilter2Payload(filterData);
    const payload = {
      ...(newFilterPayload || {}),
      createdBy: null,
      practiceId: organization.id,
    };
    const res = await PreApprovedPlanApi.PlanList({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      data: payload
    });
    let newPageData = { ...UI.pageData }
    let newRows: PreApprovedPlanListType[] = [];
    if (res?.success) {
      newRows = PreApprovedPlanFormatter.PlanList(res?.data);
      newPageData = res.criterion
    }
    setRows(newRows);
    setPageData(newPageData)
    dispatch(setPageLimit({
      id: "org-pre-approved-plan",
      limit: newPageData.limit
    }));
    dispatch(setPageFilter({
      id: "org-pre-approved-plan",
      filterData: TransformFilterDataRedox(filterData)
    }));
    setLoading(false);
  }

  const handleView: (data: PreApprovedPlanListType) => void = (data) => {
    navigate(`../${data.id}/${RouteUrls.plan}`, {
      state: { planVersionId: data.planVersionId }
    });
  };

  const isPlanVersionEnabled: (
    data: PreApprovedPlanListType
  ) => boolean = (data) => data.status?.toUpperCase() == "APPROVED";

  const handleEnableDisable: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    if (isPlanVersionEnabled(data)) {
      setDisablePlan(data);
      return;
    }
    setEnablePlan(data);
  };

  const handleDisableConfirm: () => void = async () => {
    if (!disablePlan) return;
    await confirmEnableDisable(disablePlan, true);
    setDisablePlan(null);
  };

  const confirmEnableDisable: (
    data: PreApprovedPlanListType | null,
    showDisableMessage?: boolean
  ) => void = async (data, showDisableMessage = false) => {
    if (!data) return;
    const res = await PreApprovedPlanApi.TogglePlanStatus({
      data: {
        planId: data.id,
        planVersionId: data.planVersionId
      }
    });
    if (res?.success) {
      dispatch(
        setAlertData(
          showDisableMessage
            ? {
              alertMessage:
                `Preapproved plan for ${data.problemName || "this problem"} ` +
                "has been disabled.",
              openAlert: true,
              alertVariant: "success",
            }
            : res?.alert
        )
      );
      handleSetData(pageData.page, pageData.limit);
    }
    setEnablePlan(null);
  };

  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol()}
      tableRow={rows}
      tableAction={(data: PreApprovedPlanListType) =>
        actionArr(
          () => handleView(data),
          () => handleEnableDisable(data),
          data
        )
      }
      searchText={searchText}
    />
  );

  return (
    <Box
      height="91%"
      sx={{
        ...CommonStyle.sxWhiteBox,
        overflow: "auto",
        px: 2
      }}
    >
      <ModalLayout
        modwidth="33%"
        heading={"Warning"}
        content={
          <Fragment>
            <Typography textAlign="center" fontWeight={600}>
              Are you sure you want to disable this Pre-Approved Plan?
            </Typography>
            <Typography mt={2}>
              <b>Existing Plans:</b>{" "}
              This pre-approved plan will be available for care plans that have
              already been started but are not signed off. Users can choose to
              edit or continue without editing the plan.
            </Typography>
            <Typography mt={1}>
              <b>New Plans:</b>{" "}
              This pre-approved plan will immediately become unavailable for any
              new care plans.
            </Typography>
            <Typography textAlign="center" mt={2}>
              Do you wish to proceed?
            </Typography>
          </Fragment>
        }
        open={disablePlan ? true : false}
        onClose={() => setDisablePlan(null)}
        buttonArr={CommonButtonArray(
          handleDisableConfirm,
          () => setDisablePlan(null),
          "Yes",
          "No"
        )}
      />
      <ModalLayout
        modwidth="34%"
        heading={"Enable Plan Version?"}
        content={
          <Fragment>
            <Typography textAlign="center">
              Are you sure you want to enable this plan version?
            </Typography>
            <Typography textAlign="center" mt={2}>
              Doing this will disable the existing enabled approved version.
            </Typography>
          </Fragment>
        }
        onClose={() => setEnablePlan(null)}
        open={enablePlan ? true : false}
        buttonArr={CommonButtonArray(
          () => confirmEnableDisable(enablePlan),
          () => setEnablePlan(null),
          "Yes",
          "No"
        )}
      />
      <Filter2
        filterDataArr={filterData}
        handleFilterClick={(data) => setFilterData(data)}
        changeSearchText={(val) => setSearchText(val)}
        setFilterHeight={(val) => setFilterHeight(val)}
      />
      <TableLayout
        tblBody={TblBodyJSX}
        tblHead={ControlledTable.TblHead(TableCol())}
        loading={loading}
        height={tableHeight}
        pageRecordCount={rows.length}
        totalRecordCount={pageData.total}
        Pagination={
          <ControlledTable.Pagination
            mainPage={pageData.page}
            limit={pageData.limit}
            total={pageData.total}
            handleClick={handleSetData}
            tableStyle={true}
          />
        }
      />
    </Box>
  );
};
export default PreApprovedPlan;
